import { monthlyPaymentEur, FINANCING_DEFAULTS } from '@/lib/financingEstimate';
import React, { useMemo, useState } from 'react';
import { Slider } from '@/components/ui/slider';
import { Calculator, ArrowRight } from 'lucide-react';
import credit123Logo from '@/assets/123credit-logo.png';
import { getFinancingEligibility } from '@/components/listings/detail/FinancingCTA';
import { trackEvent } from '@/lib/posthog';

interface Props {
  priceEur: number;
  /** Same price in RON (from fx_rates); null when no rate. */
  priceRon?: number | null;
  listingId: string;
  make: string;
  model?: string;
  trim?: string;
  year?: number;
  fuel?: string;
  mileage?: number;
  currency?: string;
  title?: string;
}


export const PaymentCalculatorV2: React.FC<Props> = ({
  priceEur, priceRon, listingId, make, model, trim, year, fuel, mileage, currency, title,
}) => {
  const [downPct, setDownPct] = useState<number>(FINANCING_DEFAULTS.downPct);
  const [months, setMonths] = useState<number>(FINANCING_DEFAULTS.months);

  const { monthly } = useMemo(() => {
    return { monthly: monthlyPaymentEur(priceEur, downPct, months) };
  }, [priceEur, downPct, months]);

  const eligibility = getFinancingEligibility(year, mileage, priceEur);
  // RON amount from fx_rates (caller); omitted from the link when no rate.
  const priceRON = priceRon != null && priceRon > 0 ? Math.round(priceRon) : '';
  const carTitle = title || [make, model, trim].filter(Boolean).join(' ');
  const fullTitle = year ? `${carTitle} ${year}` : carTitle;
  const params = new URLSearchParams({
    brand: make || '', make: make || '', marca: make || '',
    model: model || '', trim: trim || '', versiune: trim || '',
    year: String(year || ''), an: String(year || ''),
    price: String(priceRON), pret: String(priceRON), currency: 'RON',
    fuel: fuel || '', combustibil: fuel || '',
    mileage: String(mileage || ''), km: String(mileage || ''),
    listingId, product: eligibility || 'credit',
    title: fullTitle, car: fullTitle, vehicul: fullTitle, masina: fullTitle, descriere: fullTitle,
    avans: String(Math.round(priceEur * downPct / 100)),
    perioada: String(months),
    rata: String(Math.round(monthly)),
    utm_source: 'roautomarket', utm_medium: 'referral',
    utm_campaign: `listing_calculator_${eligibility || 'credit'}`,
  });
  const ctaUrl = `https://www.autocompare.ro/aplica-finantare?${params.toString()}`;
  const ctaLabel = eligibility === 'leasing' ? 'Aplică pentru leasing' : 'Aplică pentru credit auto';

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-xs">
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-foreground">Calculator rate</h3>
      </div>

      <div className="space-y-5">
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Avans</span>
            <span className="font-medium text-foreground">{downPct}% ({Math.round(priceEur * downPct / 100).toLocaleString('ro-RO')} €)</span>
          </div>
          <Slider value={[downPct]} onValueChange={(v) => setDownPct(v[0])} min={10} max={50} step={5} />
        </div>

        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Perioadă</span>
            <span className="font-medium text-foreground">{months} luni</span>
          </div>
          <Slider value={[months]} onValueChange={(v) => setMonths(v[0])} min={12} max={84} step={12} />
        </div>

        <div className="rounded-xl bg-muted/50 p-4 text-center">
          <div className="text-3xl font-semibold text-primary">
            {Math.round(monthly).toLocaleString('ro-RO')} €
          </div>
          <div className="text-xs text-muted-foreground mt-1">rată lunară estimată</div>
        </div>

        <a
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('finance_card_click', { listingId, make, model, year, price: priceEur, rate: Math.round(monthly), product: eligibility || 'credit', source: 'calculator' })}
          className="flex items-center justify-between gap-3 rounded-xl bg-primary text-primary-foreground px-4 py-3 hover:bg-primary/90 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold text-sm">
            {ctaLabel}
            <ArrowRight className="h-4 w-4" />
          </span>
          <span className="inline-flex items-center bg-white rounded-lg px-3 py-2">
            <img src={credit123Logo} alt="123Credit" className="h-8 w-auto" loading="lazy" />
          </span>
        </a>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Estimare orientativă la dobândă de {(FINANCING_DEFAULTS.apr * 100).toLocaleString('ro-RO')}% / an. Oferta finală prin partenerul nostru 123Credit, depinde de eligibilitate.
        </p>
      </div>
    </div>
  );
};
